import moment from "moment";

export const normalizeCampaignConfig = (payload) => {
  if (!payload) return null;
  return payload?.data ? payload.data : payload;
};

export const getRequiredFieldsFromConfig = (config, formValues = {}) => {
  const fieldRequirements = config?.field_requirements || {};
  const rewardRequirements = fieldRequirements?.reward_kind || {};
  const required = {
    reward: rewardRequirements?.[formValues?.reward_kind] || [],
    cap: [],
    dateRules: fieldRequirements?.date_rules || {},
  };

  if (formValues?.max_redemptions_per_user) {
    required.cap = fieldRequirements?.caps?.max_redemptions_per_user_requires || [];
  }

  return required;
};

export const validateCampaignForm = (config, formValues = {}) => {
  const errors = {};
  const required = getRequiredFieldsFromConfig(config, formValues);
  const shouldCompareDates = !!required?.dateRules?.start_at_before_end_at;

  if (
    shouldCompareDates &&
    formValues.start_at &&
    formValues.end_at &&
    moment(formValues.start_at).isSameOrAfter(moment(formValues.end_at))
  ) {
    errors.end_at = "End date must be after start date";
  }

  required.reward.forEach((fieldName) => {
    if (fieldName === "fee_waiver") {
      if (!formValues.fee_waiver) errors.fee_waiver = "Fee waiver must be enabled";
      return;
    }
    if (!formValues[fieldName] && formValues[fieldName] !== 0) {
      errors[fieldName] = `${fieldName.replaceAll("_", " ")} is required`;
    }
  });

  required.cap.forEach((fieldName) => {
    if (!formValues[fieldName] && formValues[fieldName] !== 0) {
      errors[fieldName] = `${fieldName.replaceAll("_", " ")} is required`;
    }
  });

  if (!formValues.name) errors.name = "Name is required";
  if (!formValues.code) errors.code = "Code is required";
  if (!formValues.campaign_type) errors.campaign_type = "Campaign type is required";
  if (!formValues.reward_kind) errors.reward_kind = "Reward kind is required";
  if (!formValues.start_at) errors.start_at = "Start date is required";
  if (!formValues.end_at) errors.end_at = errors.end_at || "End date is required";
  // Priority drives the tiebreak when two campaigns are simultaneously eligible
  // for the same transaction — the backend rejects null/blank.
  if (formValues.priority === "" || formValues.priority === null || formValues.priority === undefined) {
    errors.priority = "Priority is required";
  }

  // Backend serializer rule: setting a per-user cap without a window is invalid.
  // Surface it client-side so the form catches it before the round-trip.
  const hasPerUserCap =
    formValues.max_redemptions_per_user !== "" &&
    formValues.max_redemptions_per_user !== null &&
    formValues.max_redemptions_per_user !== undefined;
  if (hasPerUserCap && !formValues.cap_window) {
    errors.cap_window = "Cap window is required when per-user cap is set";
  }

  return errors;
};

const trimString = (value) =>
  typeof value === "string" ? value.trim() : value;

const toNullableString = (value) => {
  const trimmed = trimString(value);
  return trimmed === "" || trimmed === undefined ? null : trimmed;
};

const toNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

// Maps each known rule_type to the shape its value should take in the form and
// payload. Mirrors how SegmentBuilder treats segment rules (numeric thresholds,
// comma-separated lists, free text) so the campaign UX feels the same.
// Unknown rule types fall through to "json" — admins can still enter a raw
// value, and adding a new rule type means adding one line here, not refactoring
// the form.
const RULE_VALUE_KINDS = {
  // Numeric thresholds
  ACTIVITY_LEVEL: "number",
  SALARY_THRESHOLD: "number",
  LAST_TRANSACTION_DATE: "number",
  MIN_AGE: "number",
  MAX_AGE: "number",
  MIN_BALANCE: "number",
  MAX_BALANCE: "number",
  TX_COUNT: "number",
  AMOUNT_THRESHOLD: "number",
  // Comma-separated lists (stored on the wire as value_json arrays)
  USER_SEGMENT: "list",
  EMPLOYER: "list",
  USER_TAG: "list",
  USER_EMAILS: "list",
  COUNTRY: "list",
  CITY: "list",
  // Free-text categorical values
  USER_TIER: "text",
  ACCOUNT_TYPE: "text",
  KYC_LEVEL: "text",
};

export const getRuleValueKind = (ruleType) => RULE_VALUE_KINDS[ruleType] || "json";

export const sanitizeEligibilityRules = (rules = []) => {
  const sanitizedRules = [];
  const rowErrors = {};

  (rules || []).forEach((rule, index) => {
    const ruleType = trimString(rule?.rule_type || "");
    const operator = trimString(rule?.operator || "");

    const isCompletelyEmpty = ruleType === "" && operator === "";
    if (isCompletelyEmpty) return;

    const eachRowErrors = {};
    if (!ruleType) eachRowErrors.rule_type = "Rule type is required";
    if (!operator) eachRowErrors.operator = "Operator is required";

    // The form always stores the user's input under one of value_number /
    // value_text / value_json depending on the active rule's kind. Route the
    // chosen field into the right wire slot here, leaving the other two null
    // so a CASHBACK rule never ships with a stale FEE_WAIVER number.
    let valueFields = { value_number: null, value_text: null, value_json: null };
    const kind = getRuleValueKind(ruleType);

    if (kind === "number") {
      valueFields.value_number = toNullableNumber(rule?.value_number);
    } else if (kind === "text") {
      valueFields.value_text = toNullableString(rule?.value_text);
    } else if (kind === "list") {
      // A loaded campaign carries value_json as an already-parsed array (the wire
      // shape); the create form carries it as a comma-separated string. Handle
      // both so editing a list rule round-trips without re-splitting an array.
      if (Array.isArray(rule?.value_json)) {
        valueFields.value_json = rule.value_json
          .map((item) => (typeof item === "string" ? item.trim() : item))
          .filter((item) => item !== "" && item !== null && item !== undefined);
      } else {
        const raw = typeof rule?.value_json === "string" ? rule.value_json.trim() : "";
        if (raw) {
          valueFields.value_json = raw
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        }
      }
    } else {
      // json / unknown — admin types literal JSON, which we parse here.
      // Accept already-parsed objects/arrays untouched so an edit round-trip
      // doesn't double-parse.
      if (rule?.value_json !== null && rule?.value_json !== undefined && typeof rule.value_json !== "string") {
        valueFields.value_json = rule.value_json;
      } else {
        const raw = typeof rule?.value_json === "string" ? rule.value_json.trim() : "";
        if (raw) {
          try {
            valueFields.value_json = JSON.parse(raw);
          } catch (error) {
            eachRowErrors.value_json = "Value JSON must be valid JSON";
          }
        } else {
          // Backwards-compat: if an admin happens to have populated number/text
          // for an unknown rule_type, preserve those rather than silently dropping
          // their input.
          valueFields.value_number = toNullableNumber(rule?.value_number);
          valueFields.value_text = toNullableString(rule?.value_text);
        }
      }
    }

    if (Object.keys(eachRowErrors).length) {
      rowErrors[index] = eachRowErrors;
      return;
    }

    sanitizedRules.push({
      rule_type: ruleType,
      operator,
      logic: trimString(rule?.logic) || "AND",
      group: trimString(rule?.group) || "default",
      ...valueFields,
    });
  });

  return {
    sanitizedRules,
    rowErrors,
    hasInvalidRows: Object.keys(rowErrors).length > 0,
  };
};

export const buildCampaignPayload = (formValues = {}) => {
  const nullableOptionalFields = [
    "recurrence_type",
    "country",
    "code",
    "description",
    "notification_title",
    "notification_body",
    "banner_title",
    "banner_body",
    "reward_currency",
  ];

  const payload = {
    ...formValues,
    priority: toNullableNumber(formValues.priority),
    flat_amount: toNullableNumber(formValues.flat_amount),
    percent_value: toNullableNumber(formValues.percent_value),
    max_redemptions_per_user: toNullableNumber(formValues.max_redemptions_per_user),
    max_total_redemptions: toNullableNumber(formValues.max_total_redemptions),
    max_budget: toNullableNumber(formValues.max_budget),
    is_enabled: !!formValues.is_enabled,
    is_recurring: !!formValues.is_recurring,
    send_email: !!formValues.send_email,
    send_in_app: !!formValues.send_in_app,
    send_sms: !!formValues.send_sms,
  };

  nullableOptionalFields.forEach((key) => {
    payload[key] = toNullableString(formValues[key]);
  });

  // ISO 3166 country codes are uppercase. Some backend serializers normalize via
  // `code.split(",")` after an uppercase lookup; sending lowercase has triggered
  // a "raw.split is not a function" from a downstream parser.
  if (payload.country && typeof payload.country === "string") {
    payload.country = payload.country.trim().toUpperCase();
  }
  if (payload.reward_currency && typeof payload.reward_currency === "string") {
    payload.reward_currency = payload.reward_currency.trim().toUpperCase();
  }

  if (!payload.is_recurring) {
    payload.recurrence_type = null;
  }

  delete payload.state;

  const {
    sanitizedRules,
    rowErrors,
    hasInvalidRows,
  } = sanitizeEligibilityRules(formValues?.eligibility_rules || []);
  payload.eligibility_rules = sanitizedRules;

  return {
    payload,
    ruleErrors: rowErrors,
    hasInvalidRows,
  };
};
